/**
 * Step-up verification after email/password when the server requires 2FA.
 * Credentials stay with the parent; this only collects the code and Verify.
 */
import React, { useEffect, useRef, useState } from 'react';
import { t } from '@/i18n';
import { AuthButton, AuthInput, AuthLabel, FieldError } from './authFormControls';

interface TwoFactorVerifyModalProps {
  open: boolean;
  busy: boolean;
  error: string | null;
  onVerify: (code: string) => void;
  onCancel: () => void;
}

export function TwoFactorVerifyModal({
  open,
  busy,
  error,
  onVerify,
  onCancel,
}: TwoFactorVerifyModalProps): React.ReactElement | null {
  const [code, setCode] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) {
      setCode('');
      return;
    }
    const id = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(id);
  }, [open]);

  if (!open) return null;

  const submit = (e: React.FormEvent): void => {
    e.preventDefault();
    const trimmed = code.trim();
    if (!trimmed) return;
    onVerify(trimmed);
  };

  return (
    <>
      <div
        className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm"
        onClick={() => {
          if (!busy) onCancel();
        }}
        aria-hidden="true"
      />
      <div className="fixed inset-0 z-[61] flex items-center justify-center p-4">
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="login-2fa-title"
          className="w-full max-w-sm rounded-xl bg-white p-6 shadow-lg"
        >
          <h2 id="login-2fa-title" className="mb-2 text-lg font-semibold text-gray-900">
            {t('login.twoFactor.title')}
          </h2>
          <p className="mb-4 text-sm text-gray-600">{t('login.twoFactor.body')}</p>

          <form onSubmit={submit} className="space-y-4" noValidate>
            <div className="space-y-2">
              <AuthLabel htmlFor="totpCode" required>
                {t('login.twoFactor.codeLabel')}
              </AuthLabel>
              <AuthInput
                ref={inputRef}
                id="totpCode"
                name="totpCode"
                type="text"
                inputMode="text"
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                disabled={busy}
                error={!!error}
                aria-invalid={!!error}
                aria-describedby={error ? 'totp-error' : undefined}
              />
              <FieldError id="totp-error" message={error} />
            </div>

            <div className="flex items-center justify-end gap-3">
              <AuthButton
                type="button"
                variant="outline"
                disabled={busy}
                onClick={onCancel}
              >
                {t('login.twoFactor.cancel')}
              </AuthButton>
              <AuthButton
                type="submit"
                disabled={busy || code.trim().length < 6}
                loading={busy}
              >
                {busy ? t('login.twoFactor.verifying') : t('login.twoFactor.verify')}
              </AuthButton>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
